import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { 
  Upload, Loader2, FileText, CheckCircle2, AlertCircle, X,
  Calendar, CreditCard, Users, RefreshCw, Search, Eye, Link2, Unlink,
  ZoomIn, ZoomOut, ExternalLink, Trash2, ChevronLeft, ChevronRight
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Remises() {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  
  const [remises, setRemises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [linking, setLinking] = useState(false);
  
  // Detail modal
  const [selectedRemise, setSelectedRemise] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Manual match modal - Style Transactions
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [selectedPrelevement, setSelectedPrelevement] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [matching, setMatching] = useState(false);
  const [searchingFactures, setSearchingFactures] = useState(false);
  const [facturePage, setFacturePage] = useState(1);
  const [totalFactures, setTotalFactures] = useState(0);
  const [deleting, setDeleting] = useState(false);

  // PDF Preview modal
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfZoom, setPdfZoom] = useState(100);

  const fetchRemises = useCallback(async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/bridge-v2/remises`, { headers });
      setRemises(res.data.remises || []);
    } catch (err) {
      console.error("Erreur chargement remises:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchRemises();
  }, [fetchRemises]);

  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, results: [] });
  const [showUploadResults, setShowUploadResults] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      timeZone: 'Europe/Paris'
    });
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !token) return;
    
    const headers = { Authorization: `Bearer ${token}` };
    const results = [];
    
    // Filtrer les PDFs
    const pdfFiles = files.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    const nonPdfFiles = files.filter(f => !f.name.toLowerCase().endsWith('.pdf'));
    
    if (nonPdfFiles.length > 0) {
      results.push(...nonPdfFiles.map(f => ({
        filename: f.name,
        success: false,
        message: "Format non supporté (PDF uniquement)"
      })));
    }
    
    if (pdfFiles.length === 0) {
      if (nonPdfFiles.length > 0) {
        setUploadProgress({ current: 0, total: 0, results });
        setShowUploadResults(true);
      }
      return;
    }
    
    setUploading(true);
    setUploadProgress({ current: 0, total: pdfFiles.length, results: [] });
    
    for (let i = 0; i < pdfFiles.length; i++) {
      const file = pdfFiles[i];
      setUploadProgress(prev => ({ ...prev, current: i + 1 }));
      
      // Délai entre chaque fichier pour éviter la surcharge Gemini (même que autres modules)
      if (i > 0) {
        await new Promise(resolve => setTimeout(resolve, 2000)); // 2 secondes
      }
      
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const res = await axios.post(`${API}/api/bridge-v2/remises/import`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
        });
        results.push({
          filename: file.name,
          success: true,
          message: res.data.message || "Importé avec succès",
          remiseId: res.data.remise_id,
          nbPrelevements: res.data.nb_prelevements,
          nbRapproches: res.data.nb_rapproches,
          montantTotal: res.data.montant_total
        });
      } catch (err) {
        console.error(`Erreur upload ${file.name}:`, err);
        results.push({
          filename: file.name,
          success: false,
          message: err.response?.data?.detail || "Erreur lors de l'import"
        });
      }
    }
    
    setUploading(false);
    setUploadProgress({ current: pdfFiles.length, total: pdfFiles.length, results });
    setShowUploadResults(true);
    await fetchRemises();
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleLinkTransactions = async () => {
    if (!token || linking) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setLinking(true);
      const res = await axios.post(`${API}/api/bridge-v2/remises/link-transactions`, {}, { headers });
      alert(`${res.data.checked} remises vérifiées, ${res.data.linked} liées aux transactions Bridge`);
      await fetchRemises();
    } catch (err) {
      console.error("Erreur liaison:", err);
    } finally {
      setLinking(false);
    }
  };

  const handleViewDetail = async (remise) => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const res = await axios.get(`${API}/api/bridge-v2/remises/${remise.id}`, { headers });
      setSelectedRemise(res.data);
      setShowDetailModal(true);
    } catch (err) {
      console.error("Erreur chargement détail:", err);
    }
  };

  const handleRematch = async (remiseId) => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      await axios.post(`${API}/api/bridge-v2/remises/${remiseId}/rematch`, {}, { headers });
      await fetchRemises();
      if (selectedRemise?.id === remiseId) {
        handleViewDetail({ id: remiseId });
      }
    } catch (err) {
      console.error("Erreur rematch:", err);
    }
  };

  const handleOpenMatchModal = (prelevement) => {
    setSelectedPrelevement(prelevement);
    setSearchQuery("");
    setSearchResults([]);
    setFacturePage(1);
    setShowMatchModal(true);
    // Charger les factures immédiatement avec le montant du prélèvement
    loadFactures("", 1, prelevement);
  };

  const loadFactures = async (query = "", page = 1, prelevement = selectedPrelevement) => {
    if (!token || !prelevement) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setSearchingFactures(true);
      const montant = prelevement.montant;
      const limit = 20;
      const skip = (page - 1) * limit;
      
      // Utiliser l'API invoices avec tri intelligent
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: skip.toString(),
        priorite_montant: montant.toString()
      });
      
      if (query) params.append("search", query);
      
      const res = await axios.get(`${API}/api/invoices?${params}`, { headers });
      
      let invoices = res.data.invoices || res.data || [];
      const total = res.data.total || invoices.length;
      
      // Ajouter badges Probable et Même montant
      invoices = invoices.map(inv => {
        const isSameMontant = Math.abs((inv.total_ttc || 0) - montant) < 0.01;
        // Vérifier si le nom du client correspond
        const clientName = (inv.client?.nom || inv.client || '').toLowerCase();
        const prelevementClient = (prelevement.nom_client || '').toLowerCase();
        const isNameMatch = clientName.includes(prelevementClient) || prelevementClient.includes(clientName);
        return {
          ...inv,
          _isProbable: isSameMontant && isNameMatch,
          _isSameMontant: isSameMontant,
          _isNameMatch: isNameMatch
        };
      });
      
      // Trier : Probable d'abord, puis même montant, puis le reste
      invoices.sort((a, b) => {
        if (a._isProbable && !b._isProbable) return -1;
        if (!a._isProbable && b._isProbable) return 1;
        if (a._isSameMontant && !b._isSameMontant) return -1;
        if (!a._isSameMontant && b._isSameMontant) return 1;
        return 0;
      });
      
      setSearchResults(invoices);
      setTotalFactures(total);
    } catch (err) {
      console.error("Erreur chargement factures:", err);
      setSearchResults([]);
    } finally {
      setSearchingFactures(false);
    }
  };

  const handleSearchFactures = () => {
    setFacturePage(1);
    loadFactures(searchQuery, 1);
  };

  const handleDeleteRemise = async (remiseId) => {
    if (!token) return;
    
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette remise ?\n\nToutes les factures liées seront remises en 'Non payé'.")) {
      return;
    }
    
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setDeleting(true);
      await axios.delete(`${API}/api/bridge-v2/remises/${remiseId}`, { headers });
      setShowDetailModal(false);
      setSelectedRemise(null);
      await fetchRemises();
    } catch (err) {
      console.error("Erreur suppression:", err);
      alert(err.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  };

  const handleManualMatch = async (facture) => {
    if (!token || !selectedRemise || !selectedPrelevement || matching) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setMatching(true);
      await axios.post(`${API}/api/bridge-v2/remises/${selectedRemise.id}/match`, {
        prelevement_id: selectedPrelevement.id,
        facture_numero: facture.numero
      }, { headers });
      
      setShowMatchModal(false);
      handleViewDetail({ id: selectedRemise.id });
      await fetchRemises();
    } catch (err) {
      console.error("Erreur match:", err);
      alert(err.response?.data?.detail || "Erreur lors du rapprochement");
    } finally {
      setMatching(false);
    }
  };

  // Délier un prélèvement de sa facture
  const handleUnlinkPrelevement = async (prelevement) => {
    if (!token || !selectedRemise) return;
    
    if (!window.confirm(`Voulez-vous délier la facture ${prelevement.facture_numero} de ce prélèvement ?`)) {
      return;
    }
    
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      await axios.post(`${API}/api/bridge-v2/remises/${selectedRemise.id}/unlink`, {
        prelevement_id: prelevement.id
      }, { headers });
      
      handleViewDetail({ id: selectedRemise.id });
      await fetchRemises();
    } catch (err) {
      console.error("Erreur unlink:", err);
      alert(err.response?.data?.detail || "Erreur lors de la déliaison");
    }
  };

  // Ouvrir le PDF d'une facture
  const handleViewPdf = (factureNumero) => {
    if (!token || !factureNumero) return;
    const endpoint = `${API}/api/pdfs/${factureNumero}?token=${token}`;
    setPdfUrl(endpoint);
    setShowPdfModal(true);
  };

  // Ouvrir le PDF de la remise SEPA
  const handleViewRemisePdf = (remiseId) => {
    if (!token || !remiseId) return;
    const endpoint = `${API}/api/bridge-v2/remises/${remiseId}/pdf?token=${token}`;
    setPdfUrl(endpoint);
    setShowPdfModal(true);
  };

  const getStatutBadge = (statut) => {
    switch (statut) {
      case 'complet':
        return <Badge className="bg-green-100 text-green-700">Complet</Badge>;
      case 'partiel':
        return <Badge className="bg-orange-100 text-orange-700">Partiel</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700">Importé</Badge>;
    }
  };

  const getPrelevementColor = (p) => {
    // Handle both old and new field names for status
    const statut = p.statut || p.statut_rapprochement;
    if (statut === 'rapproche' || statut === 'rapproche_manuel') return 'bg-green-50 border-green-200';
    return 'bg-red-50 border-red-200';
  };
  
  const isPrelevementRapproche = (p) => {
    const statut = p.statut || p.statut_rapprochement;
    return statut === 'rapproche' || statut === 'rapproche_manuel';
  };

  return (
    <div className="space-y-6" data-testid="remises-page">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Remises SEPA</h1>
          <p className="text-slate-500 mt-1">Import PDF → Matching factures → Liaison transaction Bridge</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleLinkTransactions} disabled={linking}>
            {linking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
            Lier aux transactions Bridge
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".pdf"
            multiple
            className="hidden"
          />
          <Button onClick={handleFileSelect} disabled={uploading} className="bg-blue-600 hover:bg-blue-700">
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Import {uploadProgress.current}/{uploadProgress.total}...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Importer PDFs
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Barre de progression pendant l'upload */}
      {uploading && uploadProgress.total > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-blue-800">
                    Import en cours... {uploadProgress.current}/{uploadProgress.total} fichiers
                  </span>
                  <span className="text-sm text-blue-600">
                    {Math.round((uploadProgress.current / uploadProgress.total) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Explications */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-3">
          <p className="text-sm text-blue-800">
            <strong>Workflow :</strong> Importez les PDFs de remise SEPA de votre banque → L'OCR extrait les clients et montants → 
            Les factures correspondantes sont automatiquement marquées comme payées → Quand Bridge synchronise la transaction globale "REM SDD", 
            elle est liée à la remise avec le détail.
          </p>
        </CardContent>
      </Card>

      {/* Liste des remises */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : remises.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Upload className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="font-medium text-slate-900 mb-2">Aucune remise importée</h3>
            <p className="text-slate-500 text-sm mb-4">
              Importez les PDFs de remise de prélèvement SEPA de la Caisse d'Épargne
            </p>
            <Button onClick={handleFileSelect}>
              <Upload className="w-4 h-4 mr-2" />
              Importer un PDF
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {remises.map((remise) => {
            const progress = remise.nb_prelevements > 0 
              ? Math.round((remise.nb_rapproches / remise.nb_prelevements) * 100) 
              : 0;
            
            return (
              <Card 
                key={remise.id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleViewDetail(remise)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      {formatDate(remise.date_execution)}
                    </CardTitle>
                    {getStatutBadge(remise.statut)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Montant total</span>
                      <span className="font-bold text-lg">{formatCurrency(remise.montant_total)}</span>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1 text-slate-500">
                        <Users className="w-4 h-4" />
                        Clients rapprochés
                      </span>
                      <span className="font-medium">{remise.nb_rapproches} / {remise.nb_prelevements}</span>
                    </div>
                    
                    <Progress value={progress} className="h-2" />
                    
                    {/* Transaction Bridge liée */}
                    {remise.transaction_bridge_id ? (
                      <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded px-2 py-1">
                        <Link2 className="w-3 h-3" />
                        Transaction Bridge liée
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-orange-700 bg-orange-50 rounded px-2 py-1">
                        <Unlink className="w-3 h-3" />
                        En attente de sync Bridge
                      </div>
                    )}
                    
                    {/* Bouton voir PDF dans la card */}
                    {remise.pdf_file_id && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full mt-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewRemisePdf(remise.id);
                        }}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Voir PDF importé
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal Détail */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Remise du {formatDate(selectedRemise?.date_execution)}
                {selectedRemise && getStatutBadge(selectedRemise.statut)}
              </DialogTitle>
              <div className="flex items-center gap-2">
                {/* Bouton voir PDF de la remise */}
                {selectedRemise?.pdf_file_id && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewRemisePdf(selectedRemise?.id);
                    }}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Voir PDF
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => handleDeleteRemise(selectedRemise?.id)}
                  disabled={deleting}
                >
                  {deleting ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-1" />
                  )}
                  Supprimer
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          {selectedRemise && (
            <div className="flex-1 overflow-y-auto space-y-4">
              {/* Transaction Bridge liée */}
              {selectedRemise.transaction_bridge && (
                <Card className="bg-green-50 border-green-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-green-800">
                      <Link2 className="w-4 h-4" />
                      Transaction Bridge liée
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{selectedRemise.transaction_bridge.clean_description}</p>
                        <p className="text-sm text-slate-600">{formatDate(selectedRemise.transaction_bridge.date)}</p>
                      </div>
                      <p className="text-xl font-bold text-green-600">
                        {formatCurrency(selectedRemise.transaction_bridge.amount)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Stats */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold">{formatCurrency(selectedRemise.montant_total)}</p>
                  <p className="text-xs text-slate-500">Montant total</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-green-600">
                    {selectedRemise.nb_rapproches} / {selectedRemise.nb_prelevements}
                  </p>
                  <p className="text-xs text-slate-500">Clients rapprochés</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-blue-600">
                    {formatCurrency(selectedRemise.montant_rapproche)}
                  </p>
                  <p className="text-xs text-slate-500">Montant facturé</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-red-600">
                    {formatCurrency(selectedRemise.montant_total - (selectedRemise.montant_rapproche || 0))}
                  </p>
                  <p className="text-xs text-slate-500">Manquant</p>
                </div>
              </div>

              {/* Bouton rematch */}
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={() => handleRematch(selectedRemise.id)}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Relancer le matching
                </Button>
              </div>

              {/* Liste des prélèvements */}
              <div className="space-y-2">
                <h3 className="font-medium text-sm text-slate-700">Détail des prélèvements ({selectedRemise.nb_prelevements})</h3>
                {selectedRemise.prelevements?.map((p, idx) => (
                  <div 
                    key={p.id || idx}
                    className={`p-3 rounded-lg border ${getPrelevementColor(p)}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500 font-mono">#{p.rang}</span>
                          <span className="font-medium">{p.nom_client || p.nom_client_normalise}</span>
                          {isPrelevementRapproche(p) && (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          )}
                          {(p.statut === 'rapproche_manuel' || p.statut_rapprochement === 'rapproche_manuel') && (
                            <Badge className="bg-blue-100 text-blue-700 text-[10px]">Manuel</Badge>
                          )}
                        </div>
                        {/* Facture liée avec bouton voir PDF */}
                        {p.facture_numero && (
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewPdf(p.facture_numero);
                              }}
                              className="flex items-center gap-1 text-sm text-blue-600 font-medium hover:underline bg-blue-50 px-2 py-1 rounded"
                            >
                              <FileText className="w-3 h-3" />
                              {p.facture_numero}
                              <Eye className="w-3 h-3 ml-1" />
                            </button>
                            {p.facture_details && (
                              <span className="text-xs text-slate-500">
                                {p.facture_details.client && `${p.facture_details.client} •`} {formatCurrency(p.facture_details.total_ttc)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Actions à droite */}
                      <div className="text-right space-y-2">
                        <p className="font-bold">{formatCurrency(p.montant)}</p>
                        
                        <div className="flex items-center gap-1 justify-end">
                          {/* Bouton Rapprocher (pour lier ou changer) */}
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenMatchModal(p);
                            }}
                          >
                            <Search className="w-3 h-3 mr-1" />
                            {isPrelevementRapproche(p) ? 'Changer' : 'Rapprocher'}
                          </Button>
                          
                          {/* Bouton Délier (si déjà rapproché) */}
                          {isPrelevementRapproche(p) && p.facture_numero && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-7 px-2 text-xs text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUnlinkPrelevement(p);
                              }}
                              title="Délier cette facture"
                            >
                              <Unlink className="w-3 h-3 mr-1" />
                              <span className="hidden sm:inline">Délier</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Match manuel - Style Transactions */}
      <Dialog open={showMatchModal} onOpenChange={(open) => { setShowMatchModal(open); if (!open) setSearchResults([]); }}>
        <DialogContent className="max-w-3xl h-[80vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
            <DialogTitle>Rapprocher le prélèvement</DialogTitle>
          </DialogHeader>
          
          {selectedPrelevement && (
            <div className="flex-1 overflow-hidden flex flex-col px-6 min-h-0">
              {/* Prélèvement à rapprocher */}
              <div className="p-4 bg-purple-50 rounded-lg flex items-center justify-between my-4 flex-shrink-0 border border-purple-200">
                <div>
                  <p className="text-sm text-purple-600">Prélèvement à rapprocher :</p>
                  <p className="font-bold text-lg">{selectedPrelevement.nom_client}</p>
                  <p className="text-xs text-slate-500">Rang #{selectedPrelevement.rang}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-purple-700">
                    {formatCurrency(selectedPrelevement.montant)}
                  </p>
                </div>
              </div>

              {/* Barre de recherche */}
              <div className="flex gap-2 mb-4 flex-shrink-0">
                <Input
                  placeholder="Rechercher par numéro, client..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchFactures()}
                />
                <Button onClick={handleSearchFactures} disabled={searchingFactures}>
                  {searchingFactures ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>

              {/* Liste des factures - zone scrollable */}
              <div className="flex-1 overflow-y-auto border rounded-lg min-h-0">
                {searchingFactures ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>Aucune facture trouvée</p>
                    <p className="text-sm">Utilisez la recherche ou vérifiez les filtres</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Numéro</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Client</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Date</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-slate-500">Montant TTC</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-slate-500">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {searchResults.map((fac) => {
                        const clientName = fac.client?.nom || fac.client || '-';
                        const isProbable = fac._isProbable;
                        const isSameMontant = fac._isSameMontant;
                        
                        return (
                          <tr 
                            key={fac.numero}
                            className={`transition-colors ${
                              isProbable 
                                ? 'bg-green-50 border-l-4 border-l-green-500' 
                                : isSameMontant 
                                  ? 'bg-yellow-50' 
                                  : 'hover:bg-slate-50'
                            }`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleViewPdf(fac.numero)}
                                  className="font-medium text-blue-600 hover:underline flex items-center gap-1"
                                >
                                  {fac.numero}
                                  <Eye className="w-3 h-3 opacity-50" />
                                </button>
                                {isProbable && (
                                  <span className="bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded-full font-medium">
                                    ★ Probable
                                  </span>
                                )}
                                {!isProbable && isSameMontant && (
                                  <span className="bg-yellow-100 text-yellow-700 text-[10px] px-2 py-0.5 rounded-full font-medium">
                                    Même montant
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600 truncate max-w-[200px]">
                              {clientName}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-500">
                              {formatDate(fac.date)}
                            </td>
                            <td className="px-4 py-3 text-right font-medium">
                              {formatCurrency(fac.total_ttc)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Button 
                                size="sm" 
                                onClick={() => handleManualMatch(fac)}
                                disabled={matching}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                {matching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Valider"}
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Pagination factures */}
              {totalFactures > 20 && (
                <div className="flex items-center justify-between text-sm py-3 flex-shrink-0 border-t mt-2">
                  <span className="text-slate-500">{totalFactures} factures au total</span>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      disabled={facturePage === 1}
                      onClick={() => { setFacturePage(p => p - 1); loadFactures(searchQuery, facturePage - 1); }}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="px-2 py-1">Page {facturePage}</span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      disabled={facturePage * 20 >= totalFactures}
                      onClick={() => { setFacturePage(p => p + 1); loadFactures(searchQuery, facturePage + 1); }}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Footer */}
          <div className="border-t px-6 py-4 flex-shrink-0">
            <Button variant="outline" onClick={() => setShowMatchModal(false)} className="w-full">
              Annuler
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal PDF Preview */}
      <Dialog open={showPdfModal} onOpenChange={setShowPdfModal}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Prévisualisation Facture</DialogTitle>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setPdfZoom(z => Math.max(50, z - 25))}>
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-500">{pdfZoom}%</span>
                <Button variant="ghost" size="sm" onClick={() => setPdfZoom(z => Math.min(200, z + 25))}>
                  <ZoomIn className="w-4 h-4" />
                </Button>
                {pdfUrl && (
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                    <Button variant="ghost" size="sm">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </a>
                )}
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            {pdfUrl && (
              <iframe
                src={pdfUrl}
                className="w-full h-full border-0 rounded"
                style={{ transform: `scale(${pdfZoom / 100})`, transformOrigin: 'top left', width: `${10000 / pdfZoom}%`, height: `${10000 / pdfZoom}%` }}
                title="PDF Preview"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Résultats Import Bulk */}
      <Dialog open={showUploadResults} onOpenChange={setShowUploadResults}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" />
              Résultats de l'import
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Résumé */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-center">
                <p className="text-2xl font-bold text-green-600">
                  {uploadProgress.results.filter(r => r.success).length}
                </p>
                <p className="text-xs text-green-700">Importés avec succès</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-center">
                <p className="text-2xl font-bold text-red-600">
                  {uploadProgress.results.filter(r => !r.success).length}
                </p>
                <p className="text-xs text-red-700">Échecs</p>
              </div>
            </div>

            {/* Liste détaillée */}
            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {uploadProgress.results.map((result, idx) => (
                <div 
                  key={idx}
                  className={`p-3 rounded-lg border ${
                    result.success 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {result.success ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{result.filename}</p>
                      <p className={`text-xs ${result.success ? 'text-green-700' : 'text-red-700'}`}>
                        {result.message}
                      </p>
                      {result.success && result.nbPrelevements && (
                        <div className="flex gap-3 mt-1 text-xs text-slate-600">
                          <span>{result.nbPrelevements} prélèvements</span>
                          <span>{result.nbRapproches || 0} rapprochés</span>
                          <span className="font-medium">{formatCurrency(result.montantTotal)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Button 
              className="w-full" 
              onClick={() => setShowUploadResults(false)}
            >
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
